import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import { AxiosError } from 'axios';
import useHandleApiError from '../../../hooks/useHandleApiError';
import { AppExceptionResponseData } from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { CatalogCreationAccessFieldName } from '../../../modules/catalogs/creation/catalogCreationAccess.types';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import { CATALOG_ACCESS_QUERY_KEY } from './catalogAccess.keys';
import {
  addCatalogAccessEntities,
  removeCatalogAccessEntities,
  updateCatalogAccessEntitiesDates,
} from './catalogAccess.repository';
import { groupCatalogAccessChangesByDates } from './catalogAccess.utils';
import { CatalogAccessChanges, CatalogAccessFieldChanges } from './types/catalogAccess.types';

interface UseApplyCatalogAccessChangesParams {
  catalogId: string;
  onSuccess: () => void;
}

interface IsAvailableOptionsQueryKeyParams {
  queryKey: QueryKey;
  catalogId: string;
}

interface ApplyFieldChangesParams {
  catalogId: string;
  field: CatalogCreationAccessFieldName;
  changes: CatalogAccessFieldChanges;
}

const isAvailableOptionsQueryKey = ({ queryKey, catalogId }: IsAvailableOptionsQueryKeyParams) =>
  queryKey.some(
    (keyPart) =>
      typeof keyPart === 'string' &&
      keyPart.startsWith(`catalogs/${catalogId}/`) &&
      keyPart.endsWith('/available'),
  );

const throwOnFailure = (response: ApiResponse<void>) => {
  if (!response.ok) {
    throw response.originalError ?? new Error(LangEnum.GLOBAL_ERROR_MESSAGE);
  }
};

const applyFieldRemovals = async ({ catalogId, field, changes }: ApplyFieldChangesParams) => {
  if (changes.isRemovingAll) {
    throwOnFailure(await removeCatalogAccessEntities({ catalogId, field, isApplyingToAll: true }));

    return;
  }

  if (changes.removedIds.length > 0) {
    throwOnFailure(
      await removeCatalogAccessEntities({ catalogId, field, entityIds: changes.removedIds }),
    );
  }
};

const applyFieldDates = async ({ catalogId, field, changes }: ApplyFieldChangesParams) => {
  if (changes.allDates) {
    throwOnFailure(
      await updateCatalogAccessEntitiesDates({
        catalogId,
        field,
        isApplyingToAll: true,
        ...changes.allDates,
      }),
    );

    return;
  }

  for (const group of groupCatalogAccessChangesByDates([...changes.added, ...changes.updated])) {
    throwOnFailure(await updateCatalogAccessEntitiesDates({ catalogId, field, ...group }));
  }
};

const applyFieldChanges = async ({ catalogId, field, changes }: ApplyFieldChangesParams) => {
  await applyFieldRemovals({ catalogId, field, changes });

  for (const group of groupCatalogAccessChangesByDates(changes.added)) {
    throwOnFailure(await addCatalogAccessEntities({ catalogId, field, ...group }));
  }

  await applyFieldDates({ catalogId, field, changes });
};

export const useApplyCatalogAccessChanges = ({
  catalogId,
  onSuccess,
}: UseApplyCatalogAccessChangesParams) => {
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  const { isPending, mutate } = useMutation({
    scope: { id: catalogId },
    mutationFn: async (changes: CatalogAccessChanges) => {
      for (const [field, fieldChanges] of Object.entries(changes)) {
        await applyFieldChanges({
          catalogId,
          changes: fieldChanges,
          field: field as CatalogCreationAccessFieldName,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey.includes(CATALOG_ACCESS_QUERY_KEY) ||
          isAvailableOptionsQueryKey({ queryKey, catalogId }),
      });
    },
    onSuccess: () => {
      snackbarStore.handleMessage({
        type: 'success',
        message: translate(LangEnum.GLOBAL_SAVED_SUCCESSFULLY),
      });

      onSuccess();
    },
    onError: (error) => {
      const errorResponse = (error as AxiosError<AppExceptionResponseData>).response;

      snackbarStore.handleMessage({
        type: 'error',
        message: getApiErrorMessage(errorResponse ?? {}, translate(LangEnum.GLOBAL_ERROR_MESSAGE)),
      });
    },
  });

  return { isApplyingChanges: isPending, applyChanges: mutate };
};
